import { useEffect, useState } from 'react';
import {
    LayoutGrid, Home, Users, UserPlus, ShieldCheck, Shield, Package, Building2, Settings, ClipboardList,
    ClipboardCheck, SlidersHorizontal, Database, UserCog, Key, KeyRound, FileText, Briefcase, Network,
    Heart, Car, FileCheck, Baby, Contact, HeartHandshake, ScrollText, Wrench, ListChecks, Lock,
} from 'lucide-react';
import { roleService } from '../services/roleService';

const ICONS = {
    LayoutGrid, Home, Users, UserPlus, ShieldCheck, Shield, Package, Building2, Settings, ClipboardList,
    ClipboardCheck, SlidersHorizontal, Database, UserCog, Key, KeyRound, FileText, Briefcase, Network,
    Heart, Car, FileCheck, Baby, Contact, HeartHandshake, ScrollText, Wrench, ListChecks, Lock,
};

export const iconOf = (name) => ICONS[name] || Shield;

let cached = null;
let pending = null;

export function loadCatalog() {
    if (cached) return Promise.resolve(cached);
    if (!pending) {
        pending = roleService.getCatalog()
            .then((res) => {
                cached = res?.data || res;
                return cached;
            })
            .finally(() => { pending = null; });
    }
    return pending;
}

export function useCatalog() {
    const [catalog, setCatalog] = useState(cached);
    const [error, setError] = useState('');
    useEffect(() => {
        if (cached) return;
        let live = true;
        loadCatalog()
            .then((c) => live && setCatalog(c))
            .catch((e) => live && setError(e.message || 'Failed to load permissions'));
        return () => { live = false; };
    }, []);
    return { catalog, error };
}

export const modulesOf = (catalog) => (catalog?.groups || []).flatMap((g) => g.modules);

export const unitsOf = (m) => [...m.actions.map((a) => `${m.key}:${a}`), ...(m.extras || []).map((x) => x.key)];

export const allUnits = (catalog) => modulesOf(catalog).flatMap(unitsOf);

const ownerOf = (catalog, key) => {
    const mod = key.split(':')[0];
    return modulesOf(catalog).find((m) => m.key === mod);
};

export function setUnit(catalog, units, key, on) {
    const next = new Set(units);
    const m = ownerOf(catalog, key);
    const read = m ? `${m.key}:READ` : null;
    if (on) {
        next.add(key);
        if (read && key !== read && m.actions.includes('READ')) next.add(read);
    } else {
        next.delete(key);
        if (m && key === read) unitsOf(m).forEach((u) => next.delete(u));
    }
    return next;
}

export const stateOf = (units, keys) => {
    const n = keys.filter((k) => units.has(k)).length;
    return n === 0 ? false : n === keys.length ? true : 'some';
};

export function summarize(catalog, permissions = []) {
    const units = new Set(permissions);
    const out = [];
    for (const m of modulesOf(catalog)) {
        if (m.actions.some((a) => units.has(`${m.key}:${a}`))) out.push(m.label);
    }
    return out;
}

export function unitLabel(catalog, unit) {
    const m = ownerOf(catalog, unit);
    if (!m) return unit;
    const extra = (m.extras || []).find((x) => x.key === unit);
    if (extra) return `${m.label}: ${extra.label}`;
    const action = unit.split(':')[1];
    const label = catalog?.actions?.find((a) => a.key === action)?.label || action;
    return `${label} ${m.label.toLowerCase()}`;
}

export const sameSet = (a = [], b = []) => {
    const x = new Set(a);
    return x.size === new Set(b).size && b.every((k) => x.has(k));
};

export const diff = (from = [], to = []) => {
    const a = new Set(from);
    const b = new Set(to);
    return { adds: [...b].filter((k) => !a.has(k)), removes: [...a].filter((k) => !b.has(k)) };
};
